import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import MediaInput from "../../components/admin/MediaInput";

const FIELDS = [
  { key: "agency_name", label: "Agency name" },
  { key: "tagline", label: "Tagline" },
  { key: "logo_url", label: "Logo image (appears in navbar)", media: "image", folder: "logo", hint: "Upload a PNG/SVG logo, or paste any URL. Leave blank to use the text wordmark." },
  { key: "hero_headline", label: "Homepage headline", textarea: true, hint: "Use a line break for the dramatic split. First word of line 2 is highlighted in gold italic." },
  { key: "hero_subtext", label: "Homepage subtext", textarea: true },
  { key: "hero_cta_text", label: "Homepage CTA text", hint: "Label for the button under the hero subtext (e.g. 'See the work'). Hidden if left blank." },
  { key: "hero_cta_href", label: "Homepage CTA link", hint: "Where the hero CTA links to. Default: /work" },
  { key: "hero_fallback_image", label: "Hero background image (always visible)", media: "image", folder: "hero", hint: "Upload or paste a URL — dark, cinematic image. This is the base layer of the hero, always rendered, no black screen ever, even if the MP4 is empty or fails." },
  { key: "hero_mp4_fallback_url", label: "Hero background video — desktop (MP4 or Vimeo URL)", media: "video", folder: "hero", hint: "Video layered over the hero image on desktop and as the fallback on mobile if no dedicated mobile video is set. Accepts either a direct MP4 upload (≤300MB, H.264) OR a Vimeo URL (paste the share URL — audio will play when the visitor turns sound on). Leave blank to use just the image." },
  { key: "hero_mp4_fallback_url_mobile", label: "Hero background video — mobile (MP4 or Vimeo URL)", media: "video", folder: "hero", hint: "Optional dedicated video for phones (<768px viewport). Use this to upload a PORTRAIT (9:16) or square cut so the video fills the mobile screen without stretch or letterboxing. If left blank, the desktop video above is used on mobile too." },
  { key: "hero_cycle_words", label: "Headline italic cycling words", hint: "Comma-separated words. The italic gold phrase at the end of the hero headline auto-cycles through these every ~2.6s with a smooth fade. Example: keeping, watching, lasting" },
  { key: "marquee_words", label: "Discipline marquee words", hint: "Comma-separated words for the horizontal scrolling ribbon below the hero. Example: Identity, Film, Editorial, Digital, Art Direction, Campaigns, Strategy" },
  { key: "featured_heading_top", label: "Featured section — heading line 1", hint: "E.g. 'A small body'" },
  { key: "featured_heading_italic", label: "Featured section — italic gold accent (line 2)", hint: "E.g. 'of work,'" },
  { key: "featured_heading_bottom", label: "Featured section — heading line 3", hint: "E.g. 'carefully chosen.'" },
  { key: "featured_paragraph", label: "Featured section — paragraph", textarea: true },
  { key: "featured_cta_label", label: "Featured section — CTA label", hint: "E.g. 'All projects'" },
  { key: "about_heading", label: "About heading (homepage)", hint: "The regular part. E.g. 'A studio,'" },
  { key: "about_heading_italic", label: "About heading — italic gold accent", hint: "The italic gold phrase that follows. E.g. 'not an agency.'" },
  { key: "about_body", label: "About body (homepage)", textarea: true },
  { key: "work_heading", label: "Work page — heading", hint: "Title shown on the /work page. Use a period for the gold italic accent (e.g. 'The archive.' — the last word becomes italic gold automatically)." },
  { key: "work_description", label: "Work page — description", textarea: true, hint: "Supporting paragraph under the title on the /work page." },
  { key: "work_side_blur", label: "Vertical video — side blur strength (px)", type: "number", hint: "Desktop only. How blurred the cover image appears on the left/right of a portrait video. 0 = sharp, 60 = very soft. Recommended 12–30. Default 20." },
  { key: "work_side_brightness", label: "Vertical video — side brightness (%)", type: "number", hint: "Desktop only. How bright the side cover image appears. 100 = full bright, 0 = black. Lower = more cinematic / video stays the visual hero. Recommended 40–70. Default 55." },
  { key: "contact_email", label: "Contact email" },
  { key: "contact_phone", label: "Contact phone", hint: "Optional. Shown on /contact under 'Direct'. Format with country code, e.g. '+91 86678 97482'." },
  { key: "contact_address", label: "Contact address", textarea: true, hint: "Optional. Shown on /contact under 'Studio' if /admin/contact location_body is empty. Multi-line OK." },
  { key: "whatsapp_number", label: "WhatsApp number", hint: "Digits only with country code, e.g. 918667897482" },
  { key: "whatsapp_message", label: "WhatsApp pre-filled message", textarea: true },
  { key: "instagram_url", label: "Instagram URL (footer icon)", hint: "Full URL, e.g. https://instagram.com/artistryperspectives. Shows an IG icon below the email in the site footer. Leave blank to hide." },
  { key: "instagram_icon", label: "Instagram icon (optional upload)", media: "image", folder: "footer", hint: "Optional. Upload your own IG icon image (recommended: 128×128 PNG with transparent background). If provided, it replaces the default outline icon and shows larger, without any box border. Leave blank to use the default." },
  { key: "linkedin_url", label: "LinkedIn URL (footer icon)", hint: "Full URL, e.g. https://linkedin.com/company/artistry-perspectives. Shows a LinkedIn icon below the email in the site footer. Leave blank to hide." },
  { key: "linkedin_icon", label: "LinkedIn icon (optional upload)", media: "image", folder: "footer", hint: "Optional. Upload your own LinkedIn icon image (recommended: 128×128 PNG with transparent background). If provided, it replaces the default outline icon and shows larger, without any box border. Leave blank to use the default." },
  { key: "instagram", label: "Instagram URL (legacy — internal use)" },
  { key: "linkedin", label: "LinkedIn URL (legacy — internal use)" },
  { key: "twitter", label: "Twitter URL" },
  { key: "footer_note", label: "Footer fine-print", hint: "Small text shown on the right of the footer copyright strip. E.g. 'Crafted with intention.'" },
];

export default function SettingsAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setData(data || {}));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: next } = await api.put("/settings", data);
      setData(next);
      setSaved(true);
      setTimeout(() => setSaved(false), 2200);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="overline text-[#8A8A93]">Loading…</p>;

  return (
    <div data-testid="settings-admin" className="max-w-3xl">
      <p className="overline mb-2">Website content</p>
      <h1 className="font-display text-4xl md:text-5xl tracking-tighter mb-10">Content</h1>

      <form onSubmit={submit} className="space-y-6">
        {FIELDS.map(({ key, label, textarea, hint, type, options, media, folder }) => (
          <div key={key}>
            <label className="overline block mb-2">{label}</label>
            {media ? (
              <MediaInput
                value={data[key] || ""}
                onChange={(v) => set(key, v)}
                accept={media}
                folder={folder || "misc"}
                testid={`setting-${key}`}
                placeholder="https://…"
              />
            ) : type === "select" ? (
              <select
                value={data[key] || options[0].v}
                onChange={(e) => set(key, e.target.value)}
                data-testid={`setting-${key}`}
                className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
              >
                {options.map((o) => (
                  <option key={o.v} value={o.v}>{o.l}</option>
                ))}
              </select>
            ) : textarea ? (
              <textarea
                rows={3}
                value={data[key] || ""}
                onChange={(e) => set(key, e.target.value)}
                data-testid={`setting-${key}`}
                className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
              />
            ) : type === "number" ? (
              <input
                type="number"
                value={data[key] ?? ""}
                onChange={(e) => set(key, e.target.value === "" ? null : Number(e.target.value))}
                data-testid={`setting-${key}`}
                className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
              />
            ) : (
              <input
                value={data[key] || ""}
                onChange={(e) => set(key, e.target.value)}
                data-testid={`setting-${key}`}
                className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
              />
            )}
            {hint && <p className="text-xs text-[#8A8A93] mt-2">{hint}</p>}
          </div>
        ))}

        {/* ---------- Homepage section toggles ---------- */}
        <HomepageBandsToggles data={data} onChange={setData} />

        {/* ---------- Clients ribbon ---------- */}
        <ClientsEditor data={data} onChange={setData} />

        <div className="flex items-center gap-4 pt-6 border-t border-white/5">
          <button
            type="submit"
            disabled={saving}
            data-testid="settings-save"
            className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          {saved && <span className="overline text-[#C9A050]">Saved ✓</span>}
        </div>
      </form>
    </div>
  );
}

function HomepageBandsToggles({ data, onChange }) {
  // Default to true if undefined so newly added flags don't read as "off"
  const notesOn = data.home_notes_visible !== false;
  const marketingOn = data.home_marketing_visible !== false;
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <div className="pt-10 mt-8 border-t border-white/5">
      <h2 className="font-display text-2xl tracking-tight mb-1">Homepage sections</h2>
      <p className="text-xs text-[#8A8A93] mb-6">
        Toggle and edit the optional bands shown on the homepage between &ldquo;The Studio&rdquo; and the footer. These edits affect the homepage only — they do <strong>not</strong> change the /marketing or /blog pages.
      </p>

      {/* ---------- Marketing band ---------- */}
      <div className="border border-white/5 p-5 md:p-6 mb-6">
        <ToggleRow
          label="Show 'How we think' band"
          hint="Marketing approach preview band linking to /marketing."
          checked={marketingOn}
          onChange={(v) => set("home_marketing_visible", v)}
          testid="home-marketing-toggle"
        />
        {marketingOn && (
          <div className="mt-6 space-y-5 border-t border-white/5 pt-6">
            <BandTextField label="Overline" k="home_marketing_overline" data={data} set={set} placeholder="Our approach" max={40} />
            <div className="grid md:grid-cols-2 gap-5">
              <BandTextField label="Heading (regular)" k="home_marketing_heading" data={data} set={set} placeholder="How we" max={40} />
              <BandTextField label="Heading (italic gold accent)" k="home_marketing_heading_italic" data={data} set={set} placeholder="think." max={40} />
            </div>
            <BandTextField label="Paragraph" k="home_marketing_paragraph" data={data} set={set} placeholder="Not one-off campaigns…" max={260} textarea />

            <div className="pt-2 mt-2 border-t border-white/5">
              <p className="overline text-[#8A8A93] mb-4">3-step diagram</p>
              <p className="text-xs text-[#8A8A93] mb-4">Keep titles short and bodies tight — the diagram looks best with concise copy.</p>
              {[1, 2, 3].map((n) => (
                <div key={n} className="grid md:grid-cols-[200px_1fr] gap-4 mb-4">
                  <BandTextField label={`Step ${n} title`} k={`home_marketing_step${n}_title`} data={data} set={set} max={20} placeholder={["Insight","Distribution","System"][n-1]} />
                  <BandTextField label={`Step ${n} body`} k={`home_marketing_step${n}_body`} data={data} set={set} max={180} textarea placeholder="Short explanation (max 180 chars)…" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ---------- Notes band ---------- */}
      <div className="border border-white/5 p-5 md:p-6">
        <ToggleRow
          label="Show 'Notes & long-form' band"
          hint="Pulls all published blog posts into a swipeable carousel. Hidden automatically if there are no posts."
          checked={notesOn}
          onChange={(v) => set("home_notes_visible", v)}
          testid="home-notes-toggle"
        />
        {notesOn && (
          <div className="mt-6 space-y-5 border-t border-white/5 pt-6">
            <BandTextField label="Overline" k="home_notes_overline" data={data} set={set} placeholder="From the studio" max={40} />
            <div className="grid md:grid-cols-2 gap-5">
              <BandTextField label="Heading (regular)" k="home_notes_heading" data={data} set={set} placeholder="Notes &" max={40} />
              <BandTextField label="Heading (italic gold accent)" k="home_notes_heading_italic" data={data} set={set} placeholder="long-form." max={40} />
            </div>
            <BandTextField label="Paragraph" k="home_notes_paragraph" data={data} set={set} placeholder="Long-form thinking on…" max={260} textarea />
          </div>
        )}
      </div>

      {/* ---------- Hero click destination ---------- */}
      <div className="border border-white/5 p-5 md:p-6 mt-6">
        <label className="overline block mb-2">Home hero — click destination</label>
        <p className="text-xs text-[#8A8A93] mb-4">
          Where the hero video links to when someone taps it. Leave as "None" for a passive background (default).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#8A8A93] block mb-1.5">Type</label>
            <select
              value={data.hero_click_type || "none"}
              onChange={(e) => set("hero_click_type", e.target.value)}
              data-testid="hero-click-type"
              className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white"
            >
              <option value="none">None — hero is not clickable</option>
              <option value="blog">Blog post</option>
              <option value="case-study">Case study</option>
              <option value="work">Work page anchor</option>
              <option value="external">External URL</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-[#8A8A93] block mb-1.5">
              {data.hero_click_type === "external" ? "URL" : "Slug"}
            </label>
            <input
              type="text"
              value={data.hero_click_target || ""}
              onChange={(e) => set("hero_click_target", e.target.value)}
              placeholder={
                data.hero_click_type === "external"
                  ? "https://example.com/…"
                  : data.hero_click_type === "blog"
                  ? "e.g. why-editorial-branding-lasts"
                  : "e.g. elora"
              }
              disabled={!data.hero_click_type || data.hero_click_type === "none"}
              data-testid="hero-click-target"
              className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white disabled:opacity-40 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <p className="text-xs text-[#8A8A93] mt-3">
          Slug is the last part of the URL — e.g. for <span className="text-[#C9A050]">/work/elora/case-study</span> the slug is <span className="text-[#C9A050]">elora</span>. External URLs open in a new tab.
        </p>
      </div>

      {/* ---------- Work categories (editable filter pills) ---------- */}
      <div className="border border-white/5 p-5 md:p-6 mt-6">
        <label className="overline block mb-2">Work — filter categories (comma-separated)</label>
        <p className="text-xs text-[#8A8A93] mb-3">
          Controls the filter pills on the public /work page AND the picker in Works admin. "All" is always shown as the first pill on the public page. Leave blank to use the built-in defaults (Branding, Film, Social, Digital).
        </p>
        <input
          type="text"
          value={(data.work_categories || []).join(", ")}
          onChange={(e) => set("work_categories", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
          placeholder="Branding, Film, Social, Digital, Editorial, Motion, …"
          data-testid="work-categories"
          className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white"
        />
      </div>

      {/* ---------- AI tab visibility ---------- */}
      <div className="border border-white/5 p-5 md:p-6 mt-6">
        <ToggleRow
          label="Show 'AI' tab in main navigation"
          hint="Adds a top-level AI page to the nav (both desktop and mobile). Currently a placeholder — future home for AI-native work."
          checked={!!data.show_ai_tab}
          onChange={(v) => set("show_ai_tab", v)}
          testid="show-ai-tab-toggle"
        />
      </div>

      {/* ---------- Blog filter — master toggle + selective hide ---------- */}
      <div className="border border-white/5 p-5 md:p-6 mt-6">
        <ToggleRow
          label="Show tag-filter pills on /blog"
          hint="Master switch. When OFF, the entire tag filter row is hidden from the public blog page (tags remain on each post's metadata for SEO / AI engines)."
          checked={data.show_blog_filter !== false}
          onChange={(v) => set("show_blog_filter", v)}
          testid="show-blog-filter-toggle"
        />
        <div className="mt-6 border-t border-white/5 pt-5">
          <label className="overline block mb-2">…or hide only specific tags (comma-separated)</label>
          <p className="text-xs text-[#8A8A93] mb-3">
            Only used when the master switch above is ON. Tags in this list won't appear as filter pills but remain in metadata.
          </p>
          <input
            type="text"
            value={(data.hidden_blog_tags || []).join(", ")}
            onChange={(e) => set("hidden_blog_tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
            placeholder="e.g. Draft, Internal, Season 1"
            data-testid="hidden-blog-tags"
            className="w-full bg-[#121214] border border-white/10 rounded px-3 py-2 outline-none focus:border-[#C9A050] text-white"
          />
        </div>
      </div>
    </div>
  );
}

function BandTextField({ label, k, data, set, placeholder, max, textarea }) {
  const value = data[k] ?? "";
  const remaining = max ? max - value.length : null;
  const Cmp = textarea ? "textarea" : "input";
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      <Cmp
        value={value}
        onChange={(e) => set(k, e.target.value)}
        maxLength={max}
        placeholder={placeholder}
        rows={textarea ? 2 : undefined}
        data-testid={`setting-${k}`}
        className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
      />
      {max && (
        <p className={`text-[10px] mt-1 text-right ${remaining < 20 ? "text-[#C9A050]/80" : "text-[#5A5A60]"}`}>
          {value.length} / {max}
        </p>
      )}
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testid }) {
  return (
    <label className="flex items-start justify-between gap-6 cursor-pointer">
      <div>
        <p className="text-sm text-white">{label}</p>
        {hint && <p className="text-xs text-[#8A8A93] mt-1">{hint}</p>}
      </div>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="w-4 h-4 mt-1 accent-[#C9A050] shrink-0"
      />
    </label>
  );
}

function ClientsEditor({ data, onChange }) {
  const clients = Array.isArray(data.clients) ? data.clients : [];

  const addRow = () => onChange({ ...data, clients: [...clients, { name: "", logo_url: "" }] });
  const updateRow = (idx, field, value) =>
    onChange({
      ...data,
      clients: clients.map((c, i) => (i === idx ? { ...c, [field]: value } : c)),
    });
  const removeRow = (idx) =>
    onChange({ ...data, clients: clients.filter((_, i) => i !== idx) });

  return (
    <div className="pt-10 mt-8 border-t border-white/5">
      <div className="flex items-start justify-between gap-6 mb-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight mb-1">Client logo strip</h2>
          <p className="text-xs text-[#8A8A93]">
            Shown between the marquee and the featured work section on the homepage. Currently{" "}
            <span className={data.clients_visible ? "text-[#C9A050]" : "text-red-400"}>
              {data.clients_visible ? "visible" : "hidden"}
            </span>
            .
          </p>
        </div>
        <label className="flex items-center gap-3 shrink-0 cursor-pointer" data-testid="clients-visible-toggle">
          <span className="overline">Show on site</span>
          <input
            type="checkbox"
            checked={!!data.clients_visible}
            onChange={(e) => onChange({ ...data, clients_visible: e.target.checked })}
            className="w-4 h-4 accent-[#C9A050]"
          />
        </label>
      </div>

      <div className="mb-4">
        <label className="overline block mb-2">Section heading</label>
        <input
          value={data.clients_heading || ""}
          onChange={(e) => onChange({ ...data, clients_heading: e.target.value })}
          placeholder="Trusted by"
          data-testid="setting-clients_heading"
          className="w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white"
        />
      </div>

      <div className="space-y-3">
        {clients.length === 0 && (
          <p className="text-xs text-[#8A8A93] italic py-2">No clients yet. Add one below.</p>
        )}
        {clients.map((c, idx) => (
          <div key={idx} className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-3 items-start bg-white/[0.02] border border-white/5 p-3">
            <input
              placeholder="Client name"
              value={c.name || ""}
              onChange={(e) => updateRow(idx, "name", e.target.value)}
              data-testid={`client-name-${idx}`}
              className="bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2 text-sm text-white"
            />
            <MediaInput
              value={c.logo_url || ""}
              onChange={(v) => updateRow(idx, "logo_url", v)}
              accept="image"
              folder="logo"
              testid={`client-logo-${idx}`}
              placeholder="Logo image URL (PNG/SVG)"
              compact
            />
            <div className="flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={() => removeRow(idx)}
                data-testid={`client-remove-${idx}`}
                className="overline text-[#8A8A93] hover:text-red-400 px-2"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        data-testid="add-client-btn"
        className="mt-4 overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-5 py-2.5 transition-colors"
      >
        + Add client
      </button>

      <p className="text-xs text-[#8A8A93] mt-4 leading-relaxed">
        Tip: Turn off <span className="text-white">Show on site</span> while you're gathering logos.
        Once you have 6+ logos, toggle it on and hit <span className="text-white">Save changes</span>.
      </p>
    </div>
  );
}
